import "./App.css";
import { useCallback, useMemo, useRef, useState } from "react";
import { GlobalContext } from "./store/global-context";
import { AuthContextProvider } from "./store/auth-context.jsx";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { routes } from "./routes";
import Announcer from "./component/Util/Announcer/Announcer.jsx";
import { FALLBACK_MESSAGE } from "./component/Util/errorMessage";

import "./static/prime-react-custom.css";
import "primeicons/primeicons.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
// Ticket 15 (RCMC-05/-06/-20): the portal colour tokens and the one focus indicator. Imported last, so the shared
// indicator wins any tie with an equally specific rule (the tokens themselves apply wherever the file sits)
import "./static/portal-tokens.css";

function App({ createRouter = createBrowserRouter }) {
  // One message per live region, { id, text } or null (ticket 03, RCMC-17): "status" holds the
  // latest confirmation, "alert" the latest error. A message stays until dismissed or replaced.
  const [messages, setMessages] = useState({ status: null, alert: null });
  const messageId = useRef(0);

  // Unchanged API for every screen: raiseAlert(text, "error" | "success"). Text only: anything else
  // (v1.2 passed error objects, which crashed the render) becomes the generic fallback text.
  // Returns the message's id (ticket 22), so the screen that raised it can later take away that message and no other.
  const raiseAlert = useCallback((text, type) => {
    const region = type === "error" ? "alert" : "status";
    const message = typeof text === "string" && text.trim() !== "" ? text : FALLBACK_MESSAGE;
    messageId.current += 1;
    const id = messageId.current;
    setMessages((prev) => ({ ...prev, [region]: { id, text: message } }));
    return id;
  }, []);

  // Empty a region: of whatever it shows, or (with an id) only while it still shows that message — once another
  // message has replaced it, that one stays
  const dismissMessage = useCallback((region, id) => {
    setMessages((prev) => (id === undefined || prev[region]?.id === id ? { ...prev, [region]: null } : prev));
  }, []);

  // Ticket 06: a screen clears its own error once it is out of date (e.g. a form's "required fields are
  // empty" summary after a complete submit). Emptying a live region announces nothing.
  // Ticket 22: pass the id raiseAlert returned to clear only that message (Util/formSession.js useFormMessages)
  const clearAlert = useCallback((id) => dismissMessage("alert", id), [dismissMessage]);
  // Ticket 07: likewise a confirmation, e.g. "Supporting document chosen — x." once that file is dropped
  const clearStatus = useCallback((id) => dismissMessage("status", id), [dismissMessage]);

  // Stable value, so raising a message no longer re-renders every screen that reads the context
  const globalCtx = useMemo(() => ({ raiseAlert, clearAlert, clearStatus }), [raiseAlert, clearAlert, clearStatus]);

  // Built once from the shared route table. createRouter is the test seam: tests pass a memory
  // router factory; the real app always uses the browser router.
  const [router] = useState(() => createRouter(routes));

  return (
    // <Router>
      <GlobalContext.Provider value={globalCtx}>
        <AuthContextProvider>
          {/* Everything a dialog covers lives in #app-root (ticket 04 marks it inert while a dialog is
              open); the Announcer sits beside it so messages are still announced then. */}
          <div id="app-root">
            <RouterProvider router={router} />
          </div>
          <Announcer status={messages.status} alert={messages.alert} onDismiss={dismissMessage} />
        </AuthContextProvider>
      </GlobalContext.Provider>
    // </Router>
  );

  // return (
  //   <GlobalContext.Provider value={globalCtx}>
  //     <div className="App">
  //       {/* <Header /> */}
  //       <div className='dashboard-outer-container'>
  //         <SideListView />
  //         <div className="outer-box">
  //           <Outlet />
  //         </div>
  //       </div>

  //     </div>
  //   </GlobalContext.Provider>
  // );
}

export default App;
